import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import api from '../services/api';
import { RootState } from '../store/store';
import { setUser, logout as logoutAction } from '../store/authSlice';

export default function ChangePasswordPage() {
  const nav = useNavigate();
  const dispatch = useDispatch();
  const [params] = useSearchParams();
  const { user } = useSelector((s: RootState) => s.auth);
  const forced = !!user?.mustChangePassword || params.get('forced') === '1';

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const submit = async () => {
    setError('');
    if (!currentPassword || !newPassword || !confirmPassword) { setError('All fields are required'); return; }
    if (newPassword !== confirmPassword) { setError('New passwords do not match'); return; }
    if (newPassword.length < 8) { setError('New password must be at least 8 characters'); return; }
    setLoading(true);
    try {
      await api.changePassword({ currentPassword, newPassword });
      if (user) dispatch(setUser({ ...user, mustChangePassword: false }));
      setSuccess(true);
      setTimeout(() => nav('/dashboard'), 1200);
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8" style={{ background: 'linear-gradient(135deg, #02357A 0%, #015CA6 50%, #039089 100%)' }}>
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          <div className="px-8 pt-8 pb-6" style={{ background: 'linear-gradient(135deg, #02357A, #015CA6)' }}>
            <div className="flex items-center gap-3">
              <span className="text-3xl">🔑</span>
              <div>
                <p className="text-white font-bold text-base leading-tight">{forced ? 'Password Change Required' : 'Change Password'}</p>
                <p className="text-white/60 text-xs">{forced ? 'You must set a new password before continuing' : 'Update your account password'}</p>
              </div>
            </div>
          </div>

          <div className="px-8 py-7">
            {forced && (
              <div className="mb-5 p-3 bg-amber-50 border border-amber-200 text-amber-700 rounded-xl text-sm flex items-center gap-2">
                <span>⚠️</span>Your administrator requires you to set a new password before you can access the system.
              </div>
            )}
            {error && (
              <div className="mb-5 p-3 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm flex items-center gap-2">
                <span>⚠️</span>{error}
              </div>
            )}
            {success && (
              <div className="mb-5 p-3 bg-green-50 border border-green-200 text-green-700 rounded-xl text-sm flex items-center gap-2">
                <span>✅</span>Password changed. Redirecting…
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Current Password</label>
                <input value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} type="password" autoFocus
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">New Password</label>
                <input value={newPassword} onChange={e => setNewPassword(e.target.value)} type="password"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none" />
                <p className="text-xs text-gray-400 mt-1">Minimum 8 characters. Can't reuse any of your last 5 passwords.</p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Confirm New Password</label>
                <input value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} type="password" onKeyDown={e => e.key === 'Enter' && submit()}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none" />
              </div>
            </div>

            <button onClick={submit} disabled={loading}
              className="w-full mt-6 py-3 rounded-xl text-white font-semibold text-sm disabled:opacity-50 transition-all hover:opacity-90 active:scale-95"
              style={{ background: 'linear-gradient(135deg, #02357A, #015CA6)' }}>
              {loading ? 'Updating…' : 'Update Password →'}
            </button>

            {!forced && (
              <button onClick={() => nav(-1)} className="w-full mt-2 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-medium text-sm hover:border-gray-300">
                Cancel
              </button>
            )}
            {forced && (
              <button onClick={() => { dispatch(logoutAction()); localStorage.clear(); nav('/login'); }}
                className="w-full mt-2 py-2.5 text-gray-400 font-medium text-xs hover:text-gray-600">
                Sign out instead
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
